// import actions
import {
  fetchHolidays,
  setUserPreference
} from '../../../redux/actionCreators';

// import atoms
import { employeeEntityIdsFilterAtom, planUnitsAtom } from './atoms';

// import components
import DialogManager from './components/DialogManager/DialogManager';
import Header from './components/Header/Header';
import MapView from './AzureMaps/MapView';
import CardsViewPragmatic from './CardsViewPragmatic/CardsViewPragmatic';
import ListView from './ListView/ListView';
import RosterViewPragmatic from './RosterViewPragmatic/RosterViewPragmatic';
import TimelineViewPragmatic from './TimelineViewPragmatic';
import { AppointmentContextMenuManager } from './components';

// import enums
import { View, ViewContext } from './enums';

// import hooks
import { useEffect, useMemo } from 'react';
import { useDispatch, useNavParams, useSelector } from 'hooks';
import { RecoilRoot, useRecoilState } from 'recoil';
import usePlanning from './hooks/usePlanning';
import usePlanningEntity from './hooks/usePlanningEntity';

// import types
import type { PlanUnitMix } from 'components/types';
import type { ActivityPlanningProps } from './ActivityPlanning.types';

// import utils
import {
  getAvailableViews,
  getEntityPreferenceKey,
  getViewContext,
  resolveAvailableView
} from './utils';
import { getCurrentlyLoggedInEmployeeEntityId } from 'utils/misc';
import { StyledActivityPlanningBox } from './ActivityPlanning.styles';

function ActivityPlanning({ isEmployeeRole }: ActivityPlanningProps) {
  const dispatch = useDispatch();
  const { defaultActivityPlanningView } = useSelector(
    (state) => state.preferences
  );
  const {
    hasActivityPlanningViewAccess,
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasProjectTeamAccess,
    hasKidsPlanAccess
  } = useSelector(({ grants }) => grants);
  const [, setSelectedPlanUnits] = useRecoilState(planUnitsAtom);
  const [, setEmployeeEntityIds] = useRecoilState(employeeEntityIdsFilterAtom);

  const { params, setParams } = useNavParams();
  const rawView = params?.view as View;
  const context = getViewContext();

  // Resolved to its migrated/available counterpart (e.g. legacy `roster` ->
  // `roster-new`) synchronously, in the same render as the raw param --
  // `usePlanning` below reads this, not `rawView`. Resolving it later, in a
  // post-mount effect (as the view switcher still does for a grants change
  // after mount), meant the first render fetched for a view nothing renders,
  // then refetched a second time once the effect corrected the URL.
  const availableViews = useMemo(
    () =>
      getAvailableViews(
        {
          hasActivityPlanningViewAccess,
          hasWorkPlacePlanningViewAccess,
          hasMyActivitiesProjectAccess,
          hasManageOwnProjectsAccess,
          hasKidsPlanAccess
        },
        context
      ),
    [
      context,
      hasActivityPlanningViewAccess,
      hasWorkPlacePlanningViewAccess,
      hasMyActivitiesProjectAccess,
      hasManageOwnProjectsAccess,
      hasKidsPlanAccess
    ]
  );
  const view = rawView
    ? resolveAvailableView(rawView, availableViews) ?? rawView
    : rawView;

  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType =
    planningEntityTypePreference[getEntityPreferenceKey(view)];

  useEffect(() => {
    dispatch(fetchHolidays());
  }, [dispatch]);

  useEffect(() => {
    if (params && !rawView) {
      const defaultView = Object.values(View).includes(
        defaultActivityPlanningView
      )
        ? defaultActivityPlanningView
        : View.RosterPragmatic;
      // Via `setParams` rather than `location.href`: it keeps the redirect on
      // the route this instance mounted on and drops it once we have been
      // navigated away from, which is what dragged the user back (#2145).
      setParams({ view: defaultView });
    }
  }, [params, view, defaultActivityPlanningView, setParams]);

  useEffect(() => {
    if (getViewContext() === ViewContext.Own) {
      setEmployeeEntityIds([getCurrentlyLoggedInEmployeeEntityId()]);
    }
  }, [setEmployeeEntityIds]);

  const handleSelectPlanUnit = (options: PlanUnitMix[]) => {
    setSelectedPlanUnits(options);
  };

  const handleChangeView = (view: View) => {
    if (!view) return;

    dispatch(
      setUserPreference({
        key: 'defaultActivityPlanningView',
        value: view
      })
    );
  };

  const showEvents =
    planningEntityType != 'EMPLOYEE' || hasActivityPlanningViewAccess;

  // CardsPragmatic / RosterPragmatic reuse the same planning data shape as
  // their dnd-kit counterparts; `usePlanning` normalises the view internally
  // via `getEntityPreferenceKey`, so callers pass the raw view.
  const planning = usePlanning(view, showEvents);

  return (
    <StyledActivityPlanningBox
      component="section"
      overflowX={
        view === View.Roster || view === View.RosterPragmatic
          ? 'hidden'
          : 'initial'
      }
      overflowY={
        view === View.Timeline || view === View.TimelinePragmatic
          ? 'hidden'
          : 'initial'
      }
    >
      <Header
        view={view as View}
        handleSelectPlanUnit={handleSelectPlanUnit}
        handleChangeView={handleChangeView}
        isEmployeeRole={isEmployeeRole}
      />
      {(hasActivityPlanningViewAccess ||
        hasWorkPlacePlanningViewAccess ||
        (hasMyActivitiesAccess && context == ViewContext.Own) ||
        hasManageOwnProjectsAccess ||
        hasProjectTeamAccess ||
        hasKidsPlanAccess) &&
        view === View.RosterPragmatic && (
          <RosterViewPragmatic
            isMyActivities={context === ViewContext.Own}
            {...planning}
          />
        )}
      {(hasActivityPlanningViewAccess ||
        hasWorkPlacePlanningViewAccess ||
        (hasMyActivitiesAccess && context == ViewContext.Own) ||
        hasManageOwnProjectsAccess ||
        hasProjectTeamAccess) &&
        view === View.List && (
          <ListView
            data={planning.data}
            isLoading={planning.isLoading}
            employees={planning.employees}
            isMyActivities={context === ViewContext.Own}
          />
        )}
      {hasActivityPlanningViewAccess && (
        <>
          {view === View.TimelinePragmatic && (
            <TimelineViewPragmatic {...planning} />
          )}
          {view === View.CardsPragmatic && <CardsViewPragmatic {...planning} />}
          {view === View.Map && (
            <MapView planningEntities={planning.planningEntities} />
          )}
        </>
      )}
      {planning.planningEntities && (
        <AppointmentContextMenuManager
          planningEntities={planning.planningEntities}
          isEmployeePerspective={planningEntityType === 'EMPLOYEE'}
        />
      )}
    </StyledActivityPlanningBox>
  );
}

function ActivityPlanningContainer(props) {
  return (
    <RecoilRoot>
      <ActivityPlanning {...props} />
      <DialogManager />
    </RecoilRoot>
  );
}

export default ActivityPlanningContainer;
